import axios from 'axios'
import moment from 'moment'
import { getSalesOrderList } from '@/api/orderListApi'
import { GetOftenPassengerList } from '@/api/memberApi'
import { getSessionKey, getAccountInfo } from '@/utils/localdatas'
import { getBookerID, getUserInfo, getUserCompany, getUserAccount, isUsingTestUser } from '@/utils/util'

// 手机号统一显示前三后四，会员中心所有展示场景复用同一规则。
export function maskMobile(mobile) {
	const value = String(mobile || '')
	if (!value || value.length < 7) {
		return value
	}

	return value.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
}

// 证件号码按长度做脱敏，兼容身份证、护照等不同证件格式。
export function maskCredentialNumber(number) {
	const value = String(number || '').trim()
	if (!value) {
		return ''
	}

	if (value.length <= 2) {
		return value
	}

	if (value.length <= 6) {
		return `${value.slice(0, 1)}${'*'.repeat(value.length - 2)}${value.slice(-1)}`
	}

	return `${value.slice(0, 3)}${'*'.repeat(value.length - 7)}${value.slice(-4)}`
}

const mockSnapshot = {
	profile: {
		name: '陈晓航',
		initials: '陈',
		level: '钻石会员',
		lastLogin: '2025-11-18 22:16',
		company: '一起飞商旅',
		title: '企业差旅总监',
		mobile: '138****2266',
		email: 'chenxh@yqf.com',
		tags: ['企业管理员', '国际机票'],
		documents: [
			{
				type: '护照',
				number: 'E12345678',
				expiry: '2030-08-15'
			},
			{
				type: '身份证',
				number: '1101**********6723',
				expiry: '长期'
			}
		]
	},
	stats: {
		totalOrders: 268,
		upcomingTrips: 3,
		pendingInvoices: 2,
		savings: '￥3,820'
	},
	account: {
		balance: 65200,
		credit: 200000,
		points: 18200,
		journals: [
			{
				time: '2025-11-15 10:25',
				type: '差旅充值',
				amount: '+￥20,000',
				channel: '对公转账',
				remark: 'Q4 差旅预算'
			},
			{
				time: '2025-11-12 16:40',
				type: '订单扣款',
				amount: '-￥8,230',
				channel: '机票订单 FK2025111201',
				remark: 'PEK-LAX'
			}
		]
	},
	coupons: [
		{
			id: 1,
			name: '国际机票满 1000 减 200',
			expire: '2025-12-31',
			amount: 200,
			status: '可用'
		},
		{
			id: 2,
			name: '酒店满 2 晚 9 折',
			expire: '2026-02-15',
			amount: 10,
			status: '可用'
		}
	],
	invoices: [
		{
			id: 1,
			title: '北京一起飞国际旅行社有限公司',
			type: '增值税专票',
			amount: '￥12,800',
			status: '已开具',
			date: '2025-11-08'
		}
	]
}

const mockPassengers = [
	{
		name: '李天宇',
		type: '成人',
		credential: 'E99887766',
		mobile: '139****3322',
		tags: ['CEO', '候补']
	},
	{
		name: 'WANG LILY',
		type: '成人',
		credential: '护照 K22334455',
		mobile: '188****8811',
		tags: ['VIP', '美签']
	}
]

const mockOrders = [
	{
		code: 'FK20251118001',
		type: 'Flight',
		route: '北京 - 旧金山',
		status: '待确认',
		amount: '￥9,820',
		date: '2025-12-02'
	},
	{
		code: 'HT20251116003',
		type: 'Hotel',
		route: '旧金山万豪侯爵酒店',
		status: '已确认',
		amount: '￥4,120',
		date: '2025-12-02'
	},
	{
		code: 'VS20251115002',
		type: 'Visa',
		route: '美国商务签证',
		status: '材料审核中',
		amount: '￥1,600',
		date: '2025-11-20'
	}
]

const mockNotifications = [
	{
		id: 1,
		title: '北京-旧金山订单待审批',
		desc: '请在 24 小时内完成审批，航班需二次确认座位。',
		time: '2025-11-18 10:20',
		type: 'warning',
		business: '机票'
	},
	{
		id: 2,
		title: '您有新的差旅月报可查看',
		desc: '点击进入数据洞察，查看 11 月成本优化建议。',
		time: '2025-11-17 08:52',
		type: 'info'
	}
]

const DEMO_ORDER_API = 'https://feihuo.yiqifei.com/OrderAPIV2/GetSalesOrderList'
const ORDER_DETAIL_API =
	'https://feihuo.yiqifei.com/OrderAPIV2/GetSalesOrderDetailInfo'
// 测试用户BookerID已移至 util.js 的 TEST_USER_CONFIG
// 使用 getBookerID() 函数统一获取

const DEFAULT_RANGE_DAYS = 30

function getDefaultDateRange() {
	const end = moment().format('YYYY-MM-DD')
	const start = moment().subtract(DEFAULT_RANGE_DAYS, 'days').format('YYYY-MM-DD')
	return { start, end }
}

// 状态名称到状态ID的映射（如果需要）
function mapStatusToDocStatusID(status) {
	if (!status) return ''
	// 如果传入的是数字字符串，直接返回
	if (/^\d+$/.test(status)) {
		return status
	}
	// 状态名称映射（根据实际API文档调整）
	const statusMap = {
		'待支付': '',
		'已支付': '',
		'已出票': '',
		'已取消': '',
		'已完成': ''
	}
	return statusMap[status] || ''
}

function buildOrderParams(options = {}) {
	const sessionKey = getSessionKey()
	const { start, end } = getDefaultDateRange()

	// 根据新的API参数格式构建请求参数
	// 参考格式: { BookerID, PageCount, PageSize, DocStatusID, StartDate, EndDate, ProductCategoryIDs }
	// 使用统一的BookerID获取函数（优先使用传入的bookerId，然后使用登录用户，最后使用测试用户）
	const bookerID = options.bookerId || getBookerID() || ''
	
	// 使用统一的用户信息获取函数
	const userInfo = getUserInfo()
	const userCompany = getUserCompany()
	
	// 确保BookerID不为空
	if (!bookerID) {
		console.warn('[buildOrderParams] BookerID为空，可能导致API请求失败')
	}
	
	const baseParams = {
		BookerID: bookerID || '',
		PageCount: options.page || 1,
		PageSize: options.pageSize || 10,
		DocStatusID: options.docStatusId || mapStatusToDocStatusID(options.status) || '',
		StartDate: options.startDate || start,
		EndDate: options.endDate || end,
		ProductCategoryIDs: options.productCategoryIds || null
	}

	// 添加其他必要的参数（如果API需要）
	const extendedParams = {
		...baseParams,
		useraccountno: (userInfo && userInfo.useraccountno) || (userInfo && userInfo.Account) || '',
		userCompanyCode: (userCompany && userCompany.CompanyCode) || '',
		userCompanyName: (userCompany && userCompany.CompanyName) || '',
		userRoleID: (userInfo && userInfo.userRoleID) || (userInfo && userInfo.RoleID) || 0,
		userOrganizaType: (userInfo && userInfo.userOrganizaType) || (userInfo && userInfo.OrganizaType) || 'INCU',
		userBizAccount: (userInfo && userInfo.userBizAccount) || (userInfo && userInfo.BizAccount) || '',
		yqfkey: options.yqfkey || sessionKey,
		time: Date.now(),
		clientplatform: options.clientplatform || 'H5',
		mpid: options.mpid || '11'
	}

	return extendedParams
}

function unwrapResponse(result) {
	if (!result) {
		return null
	}
	return (
		result.data ||
		result.Data ||
		result.result ||
		result.Result ||
		result.payload ||
		result
	)
}

function extractOrders(payload) {
	if (!payload) {
		return {
			orders: [],
			total: 0,
			statistics: {},
			statusCounts: {}
		}
	}

	const pools = [
		payload.SalesOrders,
		payload.Items,
		payload.OrderList,
		payload.List,
		payload.Orders
	]

	let orders = pools.find(arr => Array.isArray(arr)) || []
	if (!orders.length && Array.isArray(payload)) {
		orders = payload
	}

	// 优先使用 TotalResults，这是API返回的总记录数
	const total = Number(
		payload.TotalResults || 
		payload.TotalCount || 
		payload.Total || 
		payload.RecordCount ||
		0
	)

	// 记录总数调试信息
	console.log('[ExtractOrders] 总数信息:', {
		TotalResults: payload.TotalResults,
		TotalCount: payload.TotalCount,
		Total: payload.Total,
		RecordCount: payload.RecordCount,
		finalTotal: total,
		ordersLength: orders.length,
		hasTotalResults: !!payload.TotalResults,
		willUseTotalResults: !!payload.TotalResults && total > 0
	})
	
	// 如果TotalResults存在且大于0，使用它；否则使用orders.length作为后备
	const finalTotal = (payload.TotalResults && total > 0) ? total : (orders.length || 0)

	return {
		orders,
		total: finalTotal, // 使用计算后的最终总数
		statistics: payload.Statistics || {},
		statusCounts: payload.StatusCounts || {},
		TotalResults: payload.TotalResults // 保留原始TotalResults供调试
	}
}

function buildTripIntro(item) {
	if (!item) {
		return ''
	}
	if (item.Intro) {
		return item.Intro
	}
	const depart = item.DepartureCity || item.Departure || ''
	const arrive = item.ArrivalCity || item.Arrival || ''
	if (depart && arrive) {
		return `${depart} → ${arrive}`
	}
	return ''
}

function getProductCategoryName(productCategoryId, productCategoryCName) {
	// 根据 ProductCategoryID 映射产品类型
	const categoryMap = {
		2: '酒店',
		8: '国内机票',
		9: '国际机票'
	}
	
	if (categoryMap[productCategoryId]) {
		return categoryMap[productCategoryId]
	}
	
	// 如果没有映射，使用 ProductCategoryCName
	return productCategoryCName || '其他'
}

function normalizeOrders(rawOrders = []) {
	return rawOrders.map(order => {
		const orderNo =
			order.SOShortNr ||
			order.OrderNo ||
			order.YQFOrderNo ||
			order.SalesOrderNo ||
			'--'
		const status =
			order.LatestDocStatusCName ||
			order.LatestDocStatus ||
			order.OrderStatus ||
			''
		const amount = Number(
			order.TotalAmount ||
				order.OrderTotalAmount ||
				order.Amount ||
				order.PayAmount ||
				0
		)
		
		// 从 ProductSalesOrders 中获取产品类型信息
		let productCategoryId = null
		let productCategoryCName = null
		if (Array.isArray(order.ProductSalesOrders) && order.ProductSalesOrders.length > 0) {
			const firstProduct = order.ProductSalesOrders[0]
			productCategoryId = firstProduct.ProductCategoryID
			productCategoryCName = firstProduct.ProductCategoryCName
		}
		
		// 如果没有从子订单获取到，尝试从主订单获取
		if (!productCategoryId) {
			productCategoryId = order.ProductCategoryID
			productCategoryCName = order.ProductCategoryCName
		}
		
		const productType = getProductCategoryName(productCategoryId, productCategoryCName)
		
		const introCandidates = []
		if (order.Intro) {
			introCandidates.push(order.Intro)
		}
		if (Array.isArray(order.ProductSalesOrders)) {
			order.ProductSalesOrders.forEach(item => {
				const intro = buildTripIntro(item)
				if (intro) {
					introCandidates.push(intro)
				}
			})
		}
		if (!introCandidates.length) {
			const basicIntro = buildTripIntro(order)
			if (basicIntro) {
				introCandidates.push(basicIntro)
			}
		}

		return {
			id:
				order.SalesOrderID ||
				order.OrderID ||
				order.ID ||
				orderNo ||
				Math.random().toString(36).slice(2),
			orderNo,
			bookerName: order.BookerName || order.ContactName || '未知客户',
			bookerId: order.BookerID || '',
			type: order.OrderType || order.ProductType || 'Flight',
			productCategoryId,
			productCategoryCName: productType,
			createdAt:
				order.OrderDate ||
				order.CreateTime ||
				order.CreatedTime ||
				order.Date ||
				null,
			amount,
			currency: order.Currency || 'CNY',
			status,
			statusText: status || '未知状态',
			trip: introCandidates.join(' | '),
			raw: order
		}
	})
}

function mapStatistics(statistics = {}, fallbackTotal = 0) {
	return {
		totalOrders: statistics.TotalOrders || fallbackTotal || 0,
		totalAmount: statistics.TotalAmount || 0,
		pendingPayment: statistics.PendingPayment || 0
	}
}

function buildMockResult(meta = {}) {
	// 生成更多模拟数据以支持分页测试
	const mockOrdersExtended = []
	for (let i = 0; i < 50; i++) {
		const baseOrder = mockOrders[i % mockOrders.length]
		mockOrdersExtended.push({
			...baseOrder,
			code: baseOrder.code + (i >= mockOrders.length ? String(i) : ''),
			id: baseOrder.id + (i >= mockOrders.length ? i : 0)
		})
	}

	let list = normalizeOrders(mockOrdersExtended)

	// 如果传入了分页参数，进行手动分页
	if (meta.options && (meta.options.page || meta.options.pageSize)) {
		const page = Math.max(1, parseInt(meta.options.page) || 1)
		const pageSize = Math.max(1, parseInt(meta.options.pageSize) || 10)
		const start = (page - 1) * pageSize
		const end = start + pageSize

		console.log('[Mock] 手动分页处理:', {
			total: list.length,
			page,
			pageSize,
			start,
			end,
			willReturn: list.slice(start, end).length
		})

		list = list.slice(start, end)
	}

	return {
		list,
		total: mockOrdersExtended.length,
		statistics: mapStatistics(
			{
				TotalOrders: mockOrdersExtended.length,
				TotalAmount: mockOrdersExtended.reduce((sum, item) => sum + parseFloat(item.amount?.replace(/[￥,]/g, '') || 0), 0),
				PendingPayment: mockOrdersExtended.filter(item => item.status === '待支付').length
			},
			mockOrdersExtended.length
		),
		statusCounts: {},
		source: 'mock',
		meta,
		isEmpty: false // mock 数据不为空
	}
}

function callOrderApi(params) {
	return new Promise((resolve, reject) => {
		getSalesOrderList(params, result => {
			if (result && (result.code === -2 || result.Code === -2)) {
				reject(result)
				return
			}
			resolve(result)
		})
	})
}

async function fetchDemoOrderPayload(bookerId, options = {}) {
	// 如果没有传入bookerId，使用统一的getBookerID函数获取（优先登录用户，否则测试用户）
	if (!bookerId) {
		bookerId = getBookerID()
	}
	const params = {
		BookerID: bookerId
	}
	// 演示 API 不支持分页参数，获取所有数据后手动分页
	const response = await axios.get(DEMO_ORDER_API, { params })
	const rawData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
	const payload = unwrapResponse(rawData)

	// 如果 API 返回了所有数据但没有 TotalCount，我们需要手动处理分页
	if (payload && Array.isArray(payload.SalesOrders)) {
		const allOrders = payload.SalesOrders
		const page = Math.max(1, parseInt(options.page) || 1)
		const pageSize = Math.max(1, parseInt(options.pageSize) || 10)
		const start = (page - 1) * pageSize
		const end = start + pageSize

		// logger.log('[Demo API] 手动分页处理:', {
		// 	total: allOrders.length,
		// 	page,
		// 	pageSize,
		// 	start,
		// 	end,
		// 	willReturn: allOrders.slice(start, end).length
		// })

		// 手动分页
		payload.SalesOrders = allOrders.slice(start, end)
		// 设置总数
		if (!payload.TotalCount) {
			payload.TotalCount = allOrders.length
		}
	} else if (payload && !Array.isArray(payload.SalesOrders)) {
		// 如果数据结构不是预期的，记录警告
		// logger.warn('[Demo API] 意外的数据结构:', payload)
	}

	return {
		payload,
		meta: {
			bookerId,
			endpoint: DEMO_ORDER_API,
			response: rawData,
			options
		}
	}
}

function buildResultFromPayload(payload, source, meta) {
	const extracted = extractOrders(payload)
	const list = normalizeOrders(extracted.orders)

	// 即使列表为空也返回结果对象，以便区分"真正的空数据"和"API错误"
	return {
		list: list || [],
		total: extracted.total || 0,
		statistics: mapStatistics(extracted.statistics, extracted.total || 0),
		statusCounts: extracted.statusCounts || {},
		source,
		meta,
		isEmpty: !list || list.length === 0
	}
}

/**
 * 将登录返回的账户信息映射为会员中心需要的格式
 * @param {Object} accountInfo - 从 getAccountInfo() 获取的账户信息
 * @returns {Object} 格式化后的会员快照数据
 */
function mapAccountInfoToSnapshot(accountInfo) {
	if (!accountInfo) {
		return mockSnapshot
	}

	// 获取姓名首字母作为头像显示
	const getNameInitials = (name) => {
		if (!name) return 'Y'
		// 如果是中文，取第一个字符
		if (/[\u4e00-\u9fa5]/.test(name)) {
			return name.charAt(0)
		}
		// 如果是英文，取首字母
		return name.charAt(0).toUpperCase()
	}

	// 构建标签数组
	const tags = []
	if (accountInfo.IsAdministrator) {
		tags.push('企业管理员')
	}
	if (accountInfo.DepartmentName) {
		tags.push(accountInfo.DepartmentName)
	}
	if (accountInfo.CompanyName) {
		// 可以添加公司相关的标签
	}

	// 确定会员等级（可以根据实际业务逻辑调整）
	const getMemberLevel = () => {
		// 这里可以根据实际业务逻辑判断会员等级
		// 暂时使用默认值
		return '标准会员'
	}

	const profile = {
		name: accountInfo.Name || '游客',
		initials: getNameInitials(accountInfo.Name),
		level: getMemberLevel(),
		lastLogin: accountInfo.lastLogin || moment().format('YYYY-MM-DD HH:mm'),
		company: accountInfo.CompanyName || '',
		department: accountInfo.DepartmentName || '',
		title: accountInfo.TeamName || accountInfo.DepartmentName || '',
		mobile: maskMobile(accountInfo.Mobile || accountInfo.StaffWorkPhone || ''),
		email: accountInfo.Email || '',
		tags: tags.length > 0 ? tags : ['标准会员'],
		documents: [], // 证件信息需要从其他接口获取
		avatar: accountInfo.UserHeadImg || null,
		account: accountInfo.Account || '',
		useraccountno: accountInfo.Account || accountInfo.useraccountno || '',
		companyCode: accountInfo.CompanyCode || '',
		departmentCode: accountInfo.DepartmentCode || '',
		roleId: accountInfo.RoleID || 0,
		isAdministrator: accountInfo.IsAdministrator || false
	}

	return {
		profile,
		stats: mockSnapshot.stats, // 统计数据需要从其他接口获取
		account: mockSnapshot.account, // 账户信息需要从其他接口获取
		coupons: mockSnapshot.coupons, // 优惠券需要从其他接口获取
		invoices: mockSnapshot.invoices // 发票需要从其他接口获取
	}
}

export function fetchMemberSnapshot() {
	// 优先使用登录用户信息，否则使用模拟数据
	const accountInfo = getAccountInfo()
	if (accountInfo) {
		return Promise.resolve(mapAccountInfoToSnapshot(accountInfo))
	}
	// 未登录时返回模拟数据
	return Promise.resolve(mockSnapshot)
}

/**
 * 将API返回的常旅客数据映射为组件需要的格式
 * @param {Array} apiData - API返回的常旅客列表
 * @returns {Array} 格式化后的常旅客列表
 */
function mapOftenPassengers(apiData) {
	if (!Array.isArray(apiData) || apiData.length === 0) {
		return []
	}

	return apiData.map(item => {
		// 获取姓名：优先使用中文全名，然后是英文全名，最后组合
		let name = ''
		if (item.FullName) {
			// 如果有中文全名，优先使用
			if (/[\u4e00-\u9fa5]/.test(item.FullName)) {
				name = item.FullName
			} else {
				// 英文全名
				name = item.FullName
			}
		} else if (item.LastNameCn && item.FirstNameCn) {
			// 组合中文姓名
			name = `${item.LastNameCn}${item.FirstNameCn}`
		} else if (item.LastNameEn && item.FirstNameEn) {
			// 组合英文姓名
			name = `${item.LastNameEn} ${item.FirstNameEn}`
		} else {
			name = item.FullName || ''
		}

		// 获取证件信息（从WXCredentials数组中获取第一个有效证件）
		let certNr = ''
		let certType = ''
		let certTypeName = ''
		
		if (Array.isArray(item.WXCredentials) && item.WXCredentials.length > 0) {
			const credential = item.WXCredentials[0] // 使用第一个证件
			certNr = credential.Number || ''
			certType = credential.ProofType || ''
			certTypeName = credential.ProofName || ''
		}

		// 获取电话
		const phone = item.Phone || ''

		// 映射旅客类型：ADT -> 成人, CHD -> 小孩
		const passengerAgeSection = item.PassengerAgeSection || ''
		let passengerType = '成人'
		if (passengerAgeSection === 'ADT') {
			passengerType = '成人'
		} else if (passengerAgeSection === 'CHD') {
			passengerType = '小孩'
		} else if (passengerAgeSection) {
			passengerType = passengerAgeSection
		}

		// 构建证件显示文本
		let credential = ''
		if (certNr) {
			// 列表只展示脱敏后的证件号，避免完整敏感信息泄露。
			const maskedCertNr = maskCredentialNumber(certNr)
			if (certTypeName) {
				credential = `${certTypeName} ${maskedCertNr}`
			} else if (certType) {
				credential = `${certType} ${maskedCertNr}`
			} else {
				credential = maskedCertNr
			}
		}

		// 构建标签数组
		const tags = []
		if (item.IsSelf) {
			tags.push('本人')
		}
		// 可以根据实际需求添加更多标签

		return {
			name: name,
			type: passengerType,
			credential: credential,
			// 列表手机号统一脱敏显示，编辑时仍使用 raw 中原始值。
			mobile: maskMobile(phone),
			tags: tags.length > 0 ? tags : [],
			// 保留原始数据以便后续使用
			raw: item,
			// 保留原始证件号和手机号，供搜索或后续逻辑使用。
			rawCredential: certNr,
			rawMobile: phone,
			// 额外信息
			email: item.Email || '',
			sex: item.Sex || '',
			birthDay: item.BirthDay || '',
			personCode: item.PersonCode || ''
		}
	}).filter(item => item.name) // 过滤掉没有姓名的记录
}

/**
 * 获取常旅客列表
 * @returns {Promise<Array>} 常旅客列表
 */
export function fetchPassengers() {
	return new Promise((resolve, reject) => {
		// 获取用户账号（优先登录用户，否则使用测试用户）
		const account = getUserAccount()
		
		// 构建请求参数
		const params = {
			account: account
		}

		// 调用API
		GetOftenPassengerList(params, (result) => {
			if (result && result.code === 0) {
				// API返回成功，处理数据
				let passengerData = null
				
				// 尝试多种可能的数据结构
				if (Array.isArray(result.data)) {
					// 如果 result.data 直接是数组
					passengerData = result.data
				} else if (result.data && Array.isArray(result.data.PassengerList)) {
					// 如果 result.data.PassengerList 是数组
					passengerData = result.data.PassengerList
				} else if (result.data && typeof result.data.PassengerList === 'string') {
					// 如果 result.data.PassengerList 是JSON字符串，需要解析
					try {
						passengerData = JSON.parse(result.data.PassengerList)
					} catch (e) {
						console.error('[fetchPassengers] JSON解析失败:', e)
						passengerData = null
					}
				}
				
				if (passengerData && Array.isArray(passengerData) && passengerData.length > 0) {
					// 映射数据
					const passengers = mapOftenPassengers(passengerData)
					// logger.log('[fetchPassengers] 成功获取常旅客数据:', {
					// 	originalCount: passengerData.length,
					// 	mappedCount: passengers.length,
					// 	account,
					// 	usingTestUser: isUsingTestUser()
					// })
					resolve(passengers)
				} else {
					// 数据为空或格式不符合预期
					// logger.warn('[fetchPassengers] API返回数据为空或格式不正确:', {
					// 	result,
					// 	account,
					// 	usingTestUser: isUsingTestUser()
					// })
					resolve([]) // 返回空数组而不是模拟数据
				}
			} else {
				// API返回失败，使用模拟数据
				// logger.warn('[fetchPassengers] API返回失败，使用模拟数据:', {
				// 	result,
				// 	account,
				// 	usingTestUser: isUsingTestUser()
				// })
				resolve(mockPassengers)
			}
		})
	})
}

export async function fetchOrders(options = {}) {
	if (options.forceMock) {
		return buildMockResult({ reason: 'force-mock', options })
	}

	try {
		const params = buildOrderParams(options)
		let primaryPayload = null
		let primaryMeta = null

		try {
			const response = await callOrderApi(params)
			primaryPayload = unwrapResponse(response)
			primaryMeta = {
				params,
				response
			}
		} catch (err) {
			console.warn('[MemberCenter] primary order API failed, fallback to demo endpoint.', err)
		}

		let result = null
		
		// 尝试主 API
		if (primaryPayload) {
			result = buildResultFromPayload(primaryPayload, 'api', primaryMeta)
			// 如果主 API 返回了数据（即使是空数据），直接返回，不再尝试 demo API
			if (result && !result.isEmpty) {
				return result
			}
		}

		// 如果主 API 失败或返回空数据，尝试 demo API
		if (!result || result.isEmpty) {
			try {
				const demoAttempt = await fetchDemoOrderPayload(
					options.bookerId || getBookerID(),
					{
						page: options.page || 1,
						pageSize: options.pageSize || 10
					}
				)
				const demoResult = buildResultFromPayload(
					demoAttempt.payload,
					'demo',
					demoAttempt.meta
				)
				// 如果 demo API 返回了数据（即使是空数据），使用它
				if (demoResult && !demoResult.isEmpty) {
					return demoResult
				}
				// 如果 demo API 也返回空数据，使用主 API 的结果（如果存在）
				if (result) {
					return result
				}
				// 如果两个 API 都返回空数据，返回 demo 的结果
				return demoResult
			} catch (demoError) {
				console.warn('[MemberCenter] demo order API also failed:', demoError)
				// 如果 demo API 也失败，但主 API 有结果（即使是空数据），返回主 API 的结果
				if (result) {
					return result
				}
			}
		}

		// 如果所有 API 都失败，返回 mock 数据
		return buildMockResult({
			reason: 'all-apis-failed',
			options
		})
	} catch (error) {
		console.error('[MemberCenter] fetchOrders error:', error)
		return buildMockResult({
			reason: 'api-error',
			error
		})
	}
}

export function fetchNotifications() {
	return Promise.resolve(mockNotifications)
}

function mapFlightTrips(list = []) {
	return list.map(seg => {
		// 获取航司代码（优先使用 CarrierAirline）
		const airlineCode = seg.CarrierAirline || seg.CarrierCode || seg.AirlineCode || seg.MarketingCarrier || ''
		// 获取航班号（优先使用 CarrierFlightNr）
		let flightNo = seg.CarrierFlightNr || seg.FlightNr || seg.FlightNO || ''
		
		// 如果航班号是纯数字且有机司代码，需要组合
		// 如果航班号已经包含字母（如CA1234），则直接使用
		if (flightNo && airlineCode && /^\d+$/.test(flightNo.trim())) {
			// 航班号是纯数字，需要加上航司代码
			flightNo = airlineCode + flightNo
		}
		
		return {
		route: seg.RouteName || `${seg.DepartureCity || ''} → ${seg.ArrivalCity || ''}`,
		flightNo: flightNo,
		airlineName: seg.AirlineName || seg.CarrierName || '',
		airlineCode: airlineCode,
		departureAirport: seg.DepartureAirportName || seg.DepartureAirport || '',
		departureAirportCode: seg.DepartureAirportCode || '',
		arrivalAirport: seg.ArrivalAirportName || seg.ArrivalAirport || '',
		arrivalAirportCode: seg.ArrivalAirportCode || '',
		departureCity: seg.DepartureCityName || seg.DepartureCity || '',
		arrivalCity: seg.ArrivalCityName || seg.ArrivalCity || '',
		departureTime: seg.TakeOffDate,
		arrivalTime: seg.ArrivalDate,
		cabin: [seg.CabinClass, seg.CabinCode].filter(Boolean).join(' / '),
		cabinClass: seg.CabinClass || '',
		cabinCode: seg.CabinCode || '',
		status: seg.CustomerDocStatusCName || '',
		equipment: seg.AirEquipmentType || seg.EquipmentType || '',
		duration: seg.FlightDuration || seg.Duration || '',
		flightDistance: seg.FlightDistance || '',
		pnr: seg.PNRCode || seg.PNR || '',
		ticketNo: seg.TicketNo || seg.TicketNumber || '',
		seat: seg.Seat || seg.SeatNumber || '',
		meal: seg.Meal || seg.MealType || '',
		baggage: seg.Baggage || seg.BaggageAllowance || '',
		tips: seg.EI || seg.Remark || '',
		// 保留原始数据以便后续使用
		raw: seg
		}
	})
}

function mapHotelRooms(list = [], passengers = []) {
	console.log('[mapHotelRooms] 原始数据:', {
		roomsCount: list.length,
		passengersCount: passengers.length,
		sampleRoom: list[0],
		samplePassenger: passengers[0]
	})
	
	// 根据房间ID或房间名称匹配入住人
	const roomPassengerMap = new Map()
	passengers.forEach(p => {
		const roomId = p.HotelRoomID || p.RoomID || ''
		const roomName = p.RoomName || ''
		if (roomId) {
			if (!roomPassengerMap.has(roomId)) {
				roomPassengerMap.set(roomId, [])
			}
			roomPassengerMap.get(roomId).push(p.PassengerNameMIText || p.PassengerName || '')
		} else if (roomName) {
			if (!roomPassengerMap.has(roomName)) {
				roomPassengerMap.set(roomName, [])
			}
			roomPassengerMap.get(roomName).push(p.PassengerNameMIText || p.PassengerName || '')
		}
	})
	
	// 如果没有匹配到，使用所有入住人作为后备
	const allGuestNames = passengers.map(
		p => p.PassengerNameMIText || p.PassengerName || ''
	).filter(Boolean)
	
	return list.map(room => {
		const roomId = room.HotelRoomID || room.RoomID || ''
		const roomName = room.RoomName || room.RoomTypeName || ''
		
		// 尝试匹配入住人
		let guests = []
		if (roomId && roomPassengerMap.has(roomId)) {
			guests = roomPassengerMap.get(roomId)
		} else if (roomName && roomPassengerMap.has(roomName)) {
			guests = roomPassengerMap.get(roomName)
		} else {
			guests = allGuestNames
		}
		
		// 酒店信息：只提取名称、地址、电话
		const mapped = {
			hotelName: room.HotelCName || room.HotelName || room.HotelDisplayName || room.DisplayName || room.Intro || room.HotelIntro || '',
			hotelAddress: room.HotelAddress || room.Address || room.HotelAddressDetail || '',
			hotelPhone: room.HotelPhone || room.Phone || room.ContactPhone || room.Tel || '',
			// 保留其他字段用于调试，但不显示
			roomName: room.RoomName || room.RoomTypeName || room.RoomDisplayName || room.DisplayName || '',
			checkIn: room.CheckInDate || room.ArrivalDate || room.StartDate || room.CheckIn || '',
			checkOut: room.CheckOutDate || room.DepartureDate || room.EndDate || room.CheckOut || '',
			nights: room.NightQty || room.NightCount || room.Nights || '',
			breakfast: room.Breakfast || room.BreakFast || room.BreakfastType || '',
			status: room.CustomerDocStatusCName || room.StatusName || room.Status || '',
			guests: guests.filter(Boolean)
		}
		
		console.log('[mapHotelRooms] 映射后的房间:', {
			roomId,
			roomName: room.RoomName,
			mapped
		})
		
		return mapped
	})
}

function mapTrainTrips(list = []) {
	return list.map(trip => ({
		route:
			trip.RouteName ||
			`${trip.DepartureStationName || trip.DepartureCity || ''} → ${trip.ArrivalStationName || trip.ArrivalCity || ''}`,
		trainNo: trip.TrainCode || trip.TrainNo || '',
		seatType: trip.SeatType || trip.SeatLevel || '',
		departureStation: trip.DepartureStationName || trip.DepartureStation || '',
		arrivalStation: trip.ArrivalStationName || trip.ArrivalStation || '',
		departureTime: trip.DepartureDate,
		arrivalTime: trip.ArrivalDate,
		status: trip.CustomerDocStatusCName || trip.StatusName || ''
	}))
}

function mapPassengers(list = [], category) {
	if (!Array.isArray(list) || list.length === 0) {
		return []
	}
	
	console.log('[mapPassengers] 处理旅客数据:', {
		count: list.length,
		sample: list[0]
	})
	
	return list.map(p => {
		if (!p) return null
		
		// 映射旅客类型：ADT -> 成人, CHD -> 小孩
		const passengerTypeCode = p.PassengerTypeCode || 
		                           p.Man_type_p || 
		                           p.PassengerType || 
		                           ''
		let passengerType = ''
		if (passengerTypeCode === 'ADT' || passengerTypeCode === '成人') {
			passengerType = '成人'
		} else if (passengerTypeCode === 'CHD' || passengerTypeCode === '小孩' || passengerTypeCode === '儿童') {
			passengerType = '小孩'
		} else if (passengerTypeCode) {
			passengerType = passengerTypeCode
		} else {
			passengerType = '成人' // 默认显示为"成人"
		}
		
		// 尝试多种可能的姓名字段（Passenger 是通用的，支持多种格式）
		const name = p.PassengerNameMIText || 
		             p.PassengerName || 
		             p.Name || 
		             (p.Man_xing && p.Man_ming ? `${p.Man_xing}${p.Man_ming}` : '') ||
		             (p.FirstName && p.LastName ? `${p.LastName}${p.FirstName}` : '') ||
		             ''
		
		// 尝试多种可能的证件号字段
		const certNr = p.CertNrMIText || 
		               p.CertNr || 
		               p.IDNumber || 
		               ''
		
		// 尝试多种可能的证件类型字段
		const certType = p.CertTypeCode || 
		                 p.IDTypeCode || 
		                 p.CertType || 
		                 ''
		
		// 尝试多种可能的电话字段
		const phone = p.ContactMobileMIText || 
		              p.ContactMobile || 
		              p.Phone || 
		              ''
		
		const mapped = {
			name: name,
			type: passengerType, // 显示为"成人"或"小孩"
			typeCode: passengerTypeCode, // 保留原始代码
			certType: certType,
			certNr: certNr,
			phone: phone
		}
		
		// 如果姓名为空，记录警告但不过滤（可能证件号等其他信息有用）
		if (!name) {
			console.warn('[mapPassengers] 旅客姓名为空:', p)
		}
		
		return mapped
	}).filter(p => p !== null && p.name) // 只过滤掉 null 值和没有姓名的记录
}

function mapFees(payload) {
	console.log('[mapFees] 原始费用数据:', {
		hasLegFee: Array.isArray(payload.LegFee),
		hasHotelRoomFee: Array.isArray(payload.HotelRoomFee),
		hasTrainFee: Array.isArray(payload.TrainFee),
		LegFeeCount: payload.LegFee?.length || 0,
		HotelRoomFeeCount: payload.HotelRoomFee?.length || 0,
		TrainFeeCount: payload.TrainFee?.length || 0
	})
	
	const fees = []
	
	// 机票费用
	if (Array.isArray(payload.LegFee)) {
		payload.LegFee.forEach(fee => {
			// 映射旅客类型：ADT -> 成人, CHD -> 小孩
			const passengerTypeCode = fee.PassengerTypeCode || ''
			let itemName = fee.ItemName || '机票'
			if (passengerTypeCode === 'ADT' || passengerTypeCode === '成人') {
				itemName = '成人'
			} else if (passengerTypeCode === 'CHD' || passengerTypeCode === '小孩' || passengerTypeCode === '儿童') {
				itemName = '小孩'
			} else if (passengerTypeCode) {
				itemName = passengerTypeCode
			}
			
			fees.push({
				category: 'flight',
				item: itemName,
				price: fee.TicketPrice || fee.Price || 0,
				tax: fee.Tax || 0,
				otherFee: fee.OtherFee || fee.OtherAmount || 0,
				total: fee.TotalAmount || fee.TicketPrice || 0
			})
		})
	}
	
	// 酒店费用
	if (Array.isArray(payload.HotelRoomFee)) {
		payload.HotelRoomFee.forEach(fee => {
			console.log('[mapFees] 酒店费用项:', fee)
			// 映射旅客类型：ADT -> 成人, CHD -> 小孩
			const passengerTypeCode = fee.PassengerTypeCode || ''
			let itemName = fee.RoomName || fee.ItemName || fee.RoomTypeName || '酒店'
			if (passengerTypeCode === 'ADT' || passengerTypeCode === '成人') {
				itemName = itemName === '酒店' ? '成人' : `${itemName} - 成人`
			} else if (passengerTypeCode === 'CHD' || passengerTypeCode === '小孩' || passengerTypeCode === '儿童') {
				itemName = itemName === '酒店' ? '小孩' : `${itemName} - 小孩`
			} else if (passengerTypeCode) {
				itemName = itemName === '酒店' ? passengerTypeCode : `${itemName} - ${passengerTypeCode}`
			}
			
			fees.push({
				category: 'hotel',
				item: itemName,
				price: fee.RoomAmount || fee.Price || fee.TotalAmount || 0,
				tax: fee.Tax || fee.TaxAmount || 0,
				otherFee: fee.OtherFee || fee.OtherAmount || 0,
				total: fee.TotalAmount || fee.RoomAmount || fee.Price || 0
			})
		})
	}
	
	// 如果HotelRoomFee为空，尝试从HotelRoom中提取费用信息
	if (!fees.find(f => f.category === 'hotel') && Array.isArray(payload.HotelRoom)) {
		payload.HotelRoom.forEach(room => {
			if (room.TotalAmount || room.RoomAmount || room.Price) {
				fees.push({
					category: 'hotel',
					item: room.RoomName || room.RoomTypeName || '酒店',
					price: room.RoomAmount || room.Price || 0,
					tax: room.Tax || room.TaxAmount || 0,
					otherFee: room.OtherFee || room.OtherAmount || 0,
					total: room.TotalAmount || room.RoomAmount || room.Price || 0
				})
			}
		})
	}
	
	// 火车票费用
	if (Array.isArray(payload.TrainFee)) {
		payload.TrainFee.forEach(fee => {
			// 映射旅客类型：ADT -> 成人, CHD -> 小孩
			const passengerTypeCode = fee.PassengerTypeCode || ''
			let itemName = fee.SeatType || fee.ItemName || '火车票'
			if (passengerTypeCode === 'ADT' || passengerTypeCode === '成人') {
				itemName = itemName === '火车票' ? '成人' : `${itemName} - 成人`
			} else if (passengerTypeCode === 'CHD' || passengerTypeCode === '小孩' || passengerTypeCode === '儿童') {
				itemName = itemName === '火车票' ? '小孩' : `${itemName} - 小孩`
			} else if (passengerTypeCode) {
				itemName = itemName === '火车票' ? passengerTypeCode : `${itemName} - ${passengerTypeCode}`
			}
			
			fees.push({
				category: 'train',
				item: itemName,
				price: fee.TicketPrice || fee.Price || 0,
				tax: fee.Tax || fee.TaxAmount || 0,
				otherFee: fee.OtherFee || fee.OtherAmount || 0,
				total: fee.TotalAmount || fee.TicketPrice || fee.Price || 0
			})
		})
	}
	
	console.log('[mapFees] 映射后的费用:', fees)
	return fees
}

function normalizeOrderDetail(payload) {
	if (!payload) {
		return null
	}
	const order = payload.OrderRise || {}
	const productCategoryId = order.ProductCategoryID || 0
	
	const flightTrips = mapFlightTrips(payload.TicketTrip || [])
	const hotelPassengersRaw = payload.HotelPassenger || []
	const hotelRooms = mapHotelRooms(payload.HotelRoom || [], hotelPassengersRaw)
	const trainTrips = mapTrainTrips(payload.TrainTrip || [])
	
	// Passenger 是旅客信息，机票酒店都是通用的
	// 统一使用 Passenger 作为旅客信息源，不再区分订单类型
	const passengerList = payload.Passenger || []
	
	console.log('[normalizeOrderDetail] 旅客数据源:', {
		PassengerCount: passengerList.length,
		HotelPassengerCount: hotelPassengersRaw.length,
		TrainPassengerCount: payload.TrainPassenger?.length || 0,
		samplePassenger: passengerList[0]
	})
	
	// 统一处理旅客信息，不区分订单类型
	let passengers = mapPassengers(passengerList, '')
	
	// 如果 Passenger 为空，尝试使用其他数据源作为后备
	if (passengers.length === 0) {
		console.log('[normalizeOrderDetail] Passenger 为空，尝试使用其他数据源')
		const fallbackPassengers = [
			...(hotelPassengersRaw || []),
			...(payload.TrainPassenger || [])
		]
		passengers = mapPassengers(fallbackPassengers, '')
	}
	
	// 去重：根据姓名和证件号去重
	const uniquePassengers = []
	const seen = new Set()
	passengers.forEach(p => {
		if (p && p.name) {
			const key = `${p.name}_${p.certNr || ''}`
			if (!seen.has(key)) {
				seen.add(key)
				uniquePassengers.push(p)
			}
		}
	})
	
	passengers = uniquePassengers
	
	console.log('[normalizeOrderDetail] 处理后的旅客:', {
		originalCount: passengerList.length,
		mappedCount: passengers.length,
		passengers: passengers.map(p => ({ name: p.name, type: p.type }))
	})
	
	console.log('[normalizeOrderDetail] 订单信息:', {
		productCategoryId,
		productCategoryName: order.ProductCategoryCName || '',
		hotelPassengersCount: hotelPassengersRaw.length,
		passengerCount: payload.Passenger?.length || 0,
		finalPassengersCount: passengers.length
	})
	return {
		orderNo: order.SOShortNr || '',
		displayName: order.DisplayName || '',
		status: order.CustomerDocStatusCName || '',
		statusId: order.CustomerDocStatusID,
		productCategoryId: order.ProductCategoryID,
		productCategoryName: order.ProductCategoryCName || '',
		booker: {
			name: order.BookerName || '',
			company: order.BookerCompanyName || '',
			department: order.BookerDepartmentName || ''
		},
		contact: {
			name: order.ContactPersonMIText || order.ContactPerson || '',
			// 联系人手机号只返回脱敏值，页面不再展示完整号码。
			mobile: maskMobile(order.ContactMobileMIText || order.ContactMobile || ''),
			email: order.ContactEmailRaw || ''
		},
		pricing: {
			totalAmount: order.TotalAmount || 0,
			cashCoupon: order.CashCouponAmount || 0,
			balanceAmount: order.BalanceAmount || 0,
			currency: order.CurrencyID || 0
		},
		meta: {
			bookerId: order.BookerID,
			bookerDepartment: order.BookerDepartmentName,
			bookerCompany: order.BookerCompanyName,
			orderDate: order.OrderDate,
			paymentDeadline: order.PaymentTimeLimit,
			source: order.SourceTypeName,
			remark: order.Remark || ''
		},
		flightTrips,
		hotelRooms,
		trainTrips,
		passengers,
		fees: mapFees(payload),
		messages: payload.BizOrderMessages || [],
		remarks: payload.OrderRemark || [],
		raw: payload
	}
}

export async function fetchOrderDetail(soShortNr) {
	if (!soShortNr) {
		throw new Error('SOShortNr is required')
	}
	try {
		const response = await axios.get(ORDER_DETAIL_API, {
			params: {
				SOShortNr: soShortNr
			}
		})
		const raw =
			typeof response.data === 'string'
				? JSON.parse(response.data)
				: response.data
		const payload = unwrapResponse(raw)
		const detail = normalizeOrderDetail(payload)
		if (!detail) {
			throw new Error('未获取到订单详情数据')
		}
		return {
			source: 'api',
			detail,
			meta: {
				soShortNr,
				endpoint: ORDER_DETAIL_API,
				response: raw
			}
		}
	} catch (error) {
		console.error('[MemberCenter] fetchOrderDetail error:', error)
		throw error
	}
}

